import { Component, OnInit, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import {
  DEFAULT_BILLING_COUNTRY,
  DEFAULT_PLATFORM_IRPF_PERCENT,
  DEFAULT_PLATFORM_VAT_RATE_PERCENT,
  PLATFORM_IRPF_PRESETS,
  PlatformIssuerType,
} from '../../../models/billing.model';
import { GlobalSettingsService } from '../../../services/global-settings.service';
import {
  emptyPlatformBilling,
  isValidPlatformTaxIdForIssuer,
} from '../../../shared/billing/platform-billing.utils';

@Component({
  selector: 'app-admin-platform-billing',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-platform-billing.component.html',
})
export class AdminPlatformBillingComponent implements OnInit {
  private readonly settings = inject(GlobalSettingsService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly saveError = signal<string | null>(null);
  readonly saveSuccess = signal(false);
  private readonly submitAttempted = signal(false);

  readonly irpfPresets = PLATFORM_IRPF_PRESETS;
  readonly DEFAULT_VAT = DEFAULT_PLATFORM_VAT_RATE_PERCENT;

  readonly form = this.fb.group({
    issuerType: ['self_employed' as PlatformIssuerType, Validators.required],
    withholdingEnabled: [true],
    legalName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(120)]],
    taxId: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(12)]],
    addressLine1: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(120)]],
    postalCode: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
    city: ['Madrid', [Validators.required, Validators.minLength(2), Validators.maxLength(80)]],
    province: ['Madrid', [Validators.required, Validators.minLength(2), Validators.maxLength(80)]],
    vatRatePercent: [
      DEFAULT_PLATFORM_VAT_RATE_PERCENT,
      [Validators.required, Validators.min(0), Validators.max(100)],
    ],
    irpfWithholdingPercent: [
      DEFAULT_PLATFORM_IRPF_PERCENT,
      [Validators.required, Validators.min(0), Validators.max(47)],
    ],
  });

  async ngOnInit(): Promise<void> {
    this.form.controls.issuerType.valueChanges.subscribe(() => this.applyTaxIdValidator());
    this.applyTaxIdValidator();
    await this.load();
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.loadError.set(null);
    try {
      const data = await this.settings.loadPlatformBilling();
      const base = data ?? emptyPlatformBilling();
      this.form.patchValue({
        issuerType: base.issuerType,
        withholdingEnabled: base.withholdingEnabled,
        legalName: base.legalName,
        taxId: base.taxId,
        addressLine1: base.address.line1,
        postalCode: base.address.postalCode,
        city: base.address.city,
        province: base.address.province,
        vatRatePercent: base.vatRatePercent,
        irpfWithholdingPercent: base.irpfWithholdingPercent,
      }, { emitEvent: false });
      this.applyTaxIdValidator();
      this.form.markAsPristine();
    } catch (err: unknown) {
      this.loadError.set(err instanceof Error ? err.message : 'No se pudieron cargar los datos.');
    } finally {
      this.loading.set(false);
    }
  }

  setIrpfPreset(percent: number): void {
    this.form.controls.irpfWithholdingPercent.setValue(percent);
    this.form.controls.irpfWithholdingPercent.markAsDirty();
  }

  async save(): Promise<void> {
    if (this.saving()) return;
    this.submitAttempted.set(true);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.saveError.set(this.formErrorSummary());
      return;
    }
    this.saving.set(true);
    this.saveError.set(null);
    this.saveSuccess.set(false);
    try {
      const v = this.form.getRawValue();
      const issuerType = v.issuerType ?? 'self_employed';
      await this.settings.savePlatformBilling({
        issuerType,
        withholdingEnabled: issuerType === 'self_employed' && v.withholdingEnabled === true,
        legalName: (v.legalName ?? '').trim(),
        taxId: (v.taxId ?? '').trim(),
        address: {
          line1: (v.addressLine1 ?? '').trim(),
          postalCode: (v.postalCode ?? '').trim(),
          city: (v.city ?? '').trim(),
          province: (v.province ?? '').trim(),
          country: DEFAULT_BILLING_COUNTRY,
        },
        vatRatePercent: Number(v.vatRatePercent ?? DEFAULT_PLATFORM_VAT_RATE_PERCENT),
        irpfWithholdingPercent: Number(v.irpfWithholdingPercent ?? 0),
      });
      this.form.markAsPristine();
      this.submitAttempted.set(false);
      this.saveSuccess.set(true);
    } catch (err: unknown) {
      const code = err && typeof err === 'object' && 'code' in err
        ? String((err as { code: string }).code)
        : '';
      if (code === 'permission-denied') {
        this.saveError.set('Sin permiso de administrador para guardar en /config/globalSettings.');
      } else {
        this.saveError.set(err instanceof Error ? err.message : 'No se pudo guardar.');
      }
    } finally {
      this.saving.set(false);
    }
  }

  showError(
    controlName: 'legalName' | 'taxId' | 'addressLine1' | 'postalCode' | 'city' | 'province'
      | 'vatRatePercent' | 'irpfWithholdingPercent',
  ): boolean {
    const c = this.form.controls[controlName];
    return c.invalid && (c.touched || c.dirty || this.submitAttempted());
  }

  fieldError(
    controlName: 'legalName' | 'taxId' | 'addressLine1' | 'postalCode' | 'city' | 'province'
      | 'vatRatePercent' | 'irpfWithholdingPercent',
  ): string {
    const c = this.form.controls[controlName];
    if (c.hasError('required')) return 'Campo obligatorio.';
    if (c.hasError('minlength')) return 'Demasiado corto.';
    if (c.hasError('maxlength')) return 'Demasiado largo.';
    if (c.hasError('min') || c.hasError('max')) return 'Valor fuera de rango.';
    if (c.hasError('pattern')) {
      return controlName === 'postalCode' ? 'Usa 5 dígitos (ej. 28001).' : 'Formato no válido.';
    }
    if (c.hasError('platformTaxId')) return 'CIF o NIF de la plataforma no válido.';
    return 'Revisa este campo.';
  }

  private formErrorSummary(): string {
    const fields = Object.keys(this.form.controls) as Array<keyof typeof this.form.controls>;
    const first = fields.find(f => this.form.controls[f].invalid);
    return first ? this.fieldError(first as Parameters<AdminPlatformBillingComponent['fieldError']>[0]) : 'Revisa el formulario.';
  }

  private applyTaxIdValidator(): void {
    const issuerType = () =>
      (this.form.controls.issuerType.value ?? 'self_employed') as PlatformIssuerType;
    this.form.controls.taxId.setValidators([
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(12),
      platformTaxIdValidator(issuerType),
    ]);
    this.form.controls.taxId.updateValueAndValidity({ emitEvent: false });
  }
}

function platformTaxIdValidator(
  issuerType: () => PlatformIssuerType,
): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString().trim();
    if (!raw) return null;
    return isValidPlatformTaxIdForIssuer(raw, issuerType())
      ? null
      : { platformTaxId: true };
  };
}
